import type { ClassNameType } from '@app/domains/common/types';
import { useAuth } from '@app/libs/auth';
import { Button } from '@mui/material';
import clsx from 'clsx';
import type { HTMLAttributes, PropsWithChildren } from 'react';
import { twMerge } from 'tailwind-merge';

const BenefitsList = [
    'Unlock free chapters every day',
    'Bookmark your novel and never lose track of your progress',
    'Share your thoughts with your favorite translator in the comments.',
];

const Wrapper = ({ children, ...props }: PropsWithChildren<HTMLAttributes<HTMLElement>>) => {
    const { className, ...rest } = props;

    return (
        <section
            className={twMerge(
                'shadow-anonymous-chapter-lock mx-10 max-w-[800px] rounded-[28px] bg-gradient-ww-advanced-chapter px-20 pt-24 dark:bg-[#151515] dark:bg-none sm:mx-auto sm:px-40',
                className
            )}
            {...rest}
        >
            {children}
        </section>
    );
};

const Title = ({ className, title }: ClassNameType & { title: string }) => {
    return (
        <div className={twMerge(clsx('text-center', className))}>
            <h2 className="font-set-b24 text-gray-t1 sm:font-set-b26">{title}</h2>
        </div>
    );
};

const SubTitle = ({ className, title }: ClassNameType & { title: string }) => {
    return (
        <div className={twMerge(clsx('text-center', className))}>
            <h3 className="font-set-b14 text-blue sm:font-set-b16">{title}</h3>
        </div>
    );
};

const Description = () => {
    return (
        <div className="font-set-m14 whitespace-pre-line text-center text-gray-desc sm:font-set-m15">
            <p></p>
        </div>
    );
};
function IconCheck(props: React.SVGProps<SVGSVGElement>) {
    return (
        <svg viewBox="0 0 1024 1024" fill="currentColor" height="1em" width="1em" {...props}>
            <path d="M912 190h-69.9c-9.8 0-19.1 4.5-25.1 12.2L404.7 724.5 207 474a32 32 0 00-25.1-12.2H112c-6.7 0-10.4 7.7-6.3 12.9l273.9 347c12.8 16.2 37.4 16.2 50.3 0l488.4-618.9c4.1-5.1.4-12.8-6.3-12.8z" />
        </svg>
    );
}

const Logo = () => {
    return (
        <div className="relative mt-24 flex justify-center">
            <div className="absolute bottom-0 w-80">
                <img
                    width={80}
                    height={80}
                    src="/images/lock-3d@3x.png"
                    alt="locked lock representing last hold back"
                />
            </div>
        </div>
    );
};

const Benefit = ({ benefit }: { benefit: string }) => {
    return (
        <span className="ml-20 flex items-center gap-x-5">
            <span>
                <IconCheck className="text-blue" />
            </span>
            <p className="text-base">{benefit}</p>
        </span>
    );
};

export default function AnonymousChapterLock() {
    const { login } = useAuth();

    const register = () => {
        window.open(`${process.env.VITE_REACT_APP_AUTH_AUTHORITY}/account/register`, '_blank');
    };

    return (
        <Wrapper className="relative z-10" data-posthog-params={JSON.stringify({ on: 'Anonymous Chapter Lock' })}>
            <img
                src="/images/login-left-character.png"
                className="absolute -top-1/4 left-0 -z-10 sm:top-auto sm:bottom-0"
                alt="character left login"
            />
            <img
                src="/images/login-right-character.png"
                className="absolute -top-1/4 right-0 -z-10 sm:top-auto sm:bottom-0"
                alt="character right login"
            />
            <Logo />
            <div className="h-140 flex justify-center items-center mb-30 mt-[-8px] sm:h-auto sm:mb-16 sm:mt-0">
                <div className="mx-auto w-fit space-y-8 rounded-lg bg-[rgba(0,0,0,0.4)] p-6 sm:bg-transparent">
                    <Title className="mb-4" title="Log in to continue your adventure" />
                    <SubTitle title="Other benefits you will get" />
                </div>
                <Description />
            </div>
            <div className="space-y-8 px-4 sm:px-64 sm:pt-16">
                {BenefitsList.map(benefit => (
                    <Benefit key={benefit} benefit={benefit} />
                ))}
            </div>
            <div className="flex flex-col items-center gap-y-10 py-24">
                <Button onClick={() => login()} className="h-[44px] w-[210px] sm:h-[56px]">
                    LOG IN
                </Button>
                <Button onClick={register} variant="outlined" className="h-[44px] w-[210px] sm:h-[42px]">
                    SIGN UP
                </Button>
            </div>
        </Wrapper>
    );
}
